import { Component, Input, Renderer2, ChangeDetectorRef } from '@angular/core';
import { aggregateBy } from '@progress/kendo-data-query';
import { GlobalsService } from '@app/cp/shared/services/globals.service';
import { BaseGridEditFormLinesComponent } from '@app/cp/shared/base-components/base-grid-edit-formlines.component';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { BaseMessageInfo } from '@app/cp/shared/models/BaseMessageInfo';
import { ConstructionService } from '@app/cp/shared/services/construction.service';
import { CommonFunctions } from '@app/cp/shared/static/common-functions';
import { MSCLFilterClass } from '@app/cp/shared/classes/filters/MSCLFilterClass';
import { DispatchSixZeroSixAdjustmentBus } from '@app/cp/shared/models/DispatchSixZeroSixAdjustmentBus';
import { FeatureFlagService } from "@app/cp/shared/services/feature-flag.service";
import { UseFeatureFlagSet, IFeatureFlagSet } from '@app/cp/shared/decorators/featureflags/featureflagset-decorator';

/*******************************************************************************************************
 * Name: DispatchCostAdjustmentsComponent
 * Creator: GRG
 * Date: 20210330
 * OverridesAndExtras:
 *      1) addModeOnly input - can be used so that grid does not show and only popup window shows
 *      2) dispatchId set input property - if set then windows shows and saves with that id
 *      3) after set edit - used because there is no editing of this so set readonly to show as view
 *      4) originalReadOnly - so can return readonly to original state if viewing from grid
 *      5) customCanSaveForm - if code is 'Other' then require note
 * Notes:
 * Modified:
 *******************************************************************************************************/

@UseFeatureFlagSet()
@Component({
    selector: 'cp-dispatch-sixzerosix-adjustments',
    templateUrl: './dispatch-sixzerosix-adjustments.component.html',
    styleUrls: ['./dispatch-sixzerosix-adjustments.component.scss']
})
export class DispatchSixzerosixAdjustmentsComponent extends BaseGridEditFormLinesComponent<DispatchSixZeroSixAdjustmentBus, null, ConstructionService> {

    compFeatureFlagSet: IFeatureFlagSet = this as any; //Move this inside class
    costCodeCF: MSCLFilterClass;

    @Input() addModeOnly: boolean = false; //Do not show grid -
    @Input() multiAddMode: boolean = false; //Multiple Job Adjustment -
    private _dispatchId: any;
    @Input() set dispatchId(id: any) {
        if (CommonFunctions.hasGuidValue(id)) {
            this._parentId = id;
            this._dispatchId = id;
            this.addHandler();
        }
    }
    get dispatchId() {
        return this._dispatchId;
    }
    private _jobIds: any;
    @Input() set jobIds(ids: any[]) {
        this._jobIds = ids;
        if (this.multiAddMode && ids) {
            this.createDynamicFormFields();
        }
    }
    get jobIds() {
        return this._jobIds;
    }
    private _jobNbrs: any;
    @Input() set jobNbrs(ids: any[]) {
        this._jobNbrs = ids;
    }
    get jobNbrs() {
        return this._jobNbrs;
    }
    get noteCancelButtonText(): string {
        return this.readOnly ? "Ok" : "Cancel";
    }
    afterSetEdit() {
        this.originalReadOnly = this.readOnly;
        this.readOnly = !this.isNew;
    }
    protected closeEditor(): void {
        super.closeEditor();
        this.readOnly = this.originalReadOnly;
    }
    get dispatchCostAdjustmentCodeId(): any {
        return this.hasEditForm ? this.editForm.value.dispatchCostAdjustmentCodeId : null;
    }
    originalReadOnly: boolean;
    customCanSaveForm(): boolean {
        let canSave: boolean = true;
        return canSave;
    }

    constructor(globals: GlobalsService, renderer: Renderer2, cd: ChangeDetectorRef, svc_data: ConstructionService, featureFlagService: FeatureFlagService) {
        super(globals, renderer, cd, featureFlagService);
        this.svc_data = svc_data;
        this.className = "DispatchSixZeroSixAdjustmentBus";
        this.appCode = "ContractSixZeroSixAdjustments";
        this.appName = "Contract 606 Adjustment(s)";
        this.aggregates = [
            { field: 'amount', aggregate: 'sum' }
        ];
        this.disableCpSearchDialog();
    }
    createForm() {
        this.editForm = new UntypedFormGroup({
            'dispatchSixZeroSixAdjustmentId': new UntypedFormControl(),
            //'dispatchSixZeroSixAdjustmentCodeId': new FormControl('', Validators.required),
            'dispatchId': new UntypedFormControl(),
            'personId': new UntypedFormControl(),
            'note': new UntypedFormControl('', Validators.required),
            'isSixZeroSix': new UntypedFormControl(),//'', Validators.required),
            'insertDt': new UntypedFormControl(),
            'insertUserId': new UntypedFormControl(),
            'updateDt': new UntypedFormControl(),
            'updateUserId': new UntypedFormControl()
        });
    }
    createDynamicFormFields() {
        if (this.jobIds && this.jobIds.length > 0) {
            this.jobIds.forEach((element, index) => {
                this.editForm.addControl('subCheck-' + index, new UntypedFormControl());
            });
        }
    }
    setForm() {
        this.editForm.patchValue({
            dispatchSixZeroSixAdjustmentId: this.selectedData.dispatchSixZeroSixAdjustmentId,
            //dispatchSixZeroSixAdjustmentCodeId: this.selectedData.dispatchSixZeroSixAdjustmentCodeId,
            dispatchId: this.selectedData.dispatchId,
            personId: this.selectedData.personId,
            note: this.selectedData.note,
            isSixZeroSix: this.selectedData.isSixZeroSix,
            insertDt: this.selectedData.insertDt,
            insertUserId: this.selectedData.insertUserId,
            updateDt: this.selectedData.updateDt,
            updateUserId: this.selectedData.updateUserId
        });
    }
    sixZeroSixCheckBoxChanged(e: any) {
        this.editForm.patchValue({
            isSixZeroSix: e.target.checked
        });
    }
    protected getChangedObject(dataObj: DispatchSixZeroSixAdjustmentBus) {
        let busObj: DispatchSixZeroSixAdjustmentBus = new DispatchSixZeroSixAdjustmentBus(dataObj);
        busObj.setBeforeSave();
        return busObj;
    }
    protected save(dataObj: DispatchSixZeroSixAdjustmentBus) {
        if (this.multiAddMode) {
            this.multiSave(dataObj);
        } else {
            let busObj: DispatchSixZeroSixAdjustmentBus = this.getChangedObject(dataObj);
            var isAdded = busObj.isAdded;
            this.svc_data.saveDispatchSixZeroSixAdjustment(busObj).subscribe((info: BaseMessageInfo) => {
                if (!info.saveError) {
                    if (!this.addModeOnly) {
                        this.removeLocalAdded();
                    }
                    let newDataObj: DispatchSixZeroSixAdjustmentBus = new DispatchSixZeroSixAdjustmentBus(info.primaryReturn);
                    this.saveSuccess(newDataObj);
                    if (!this.addModeOnly) {
                        this.doParentAction(this.parentId, "GETPAGEDATA", "DISPCOSTADJ");
                    }
                }
                else {
                    this.saveError(dataObj, "Error Saving");
                }
            });
        }
    }
    public multiSave(dataObj: DispatchSixZeroSixAdjustmentBus) {
        this.jobIds.forEach((jobId, index) => {
            dataObj.dispatchId = jobId;
            //dataObj.isSixZeroSix = this.editForm.get([`subCheck-${index}`]).value;
            let busObj: DispatchSixZeroSixAdjustmentBus = this.getChangedObject(dataObj);
            var isAdded = busObj.isAdded;
            this.svc_data.saveDispatchSixZeroSixAdjustment(busObj).subscribe((info: BaseMessageInfo) => {
                if (!info.saveError) {
                    if (!this.addModeOnly) {
                        this.removeLocalAdded();
                    }
                    let newDataObj: DispatchSixZeroSixAdjustmentBus = new DispatchSixZeroSixAdjustmentBus(info.primaryReturn);
                    this.saveSuccess(newDataObj);
                    if (!this.addModeOnly) {
                        this.doParentAction(this.parentId, "GETPAGEDATA", "DISPCOSTADJ");
                    }
                }
                else {
                    this.saveError(dataObj, "Error Saving");
                }
            });
        });
    }
    deleteIndividual(data: DispatchSixZeroSixAdjustmentBus) {
        if (data.hasId) {

        }
        else {
            this.closeEditor();
        }
    }
    get canEditNote(): boolean {
        let can: boolean = true;
        return can;
    }
    processGridData() {
        this.costCodeCF = new MSCLFilterClass('dispatchCostAdjustmentCode', 'dispatchCostAdjustmentCode', this.gridData);
        if (CommonFunctions.hasArrayElementValue(this.gridData)) {
            this.aggregateResults = aggregateBy(this.gridData, this.aggregates);
        }
    }

}

